/**
 * NotificationBell —— 全局顶栏通知入口（挂载于 GlobalTopBarActions）。
 *
 * 职责：
 *  - 全局唯一挂载 useNotificationStream：维护 /notifications/stream SSE 连接，
 *    事件到达 invalidate inbox + unread；断线超限降级 60s 轮询，恢复后切回；
 *  - 角标 = 未读数（GET /inbox/unread-count）；
 *  - 打开 NotificationDrawer（降级时把轮询间隔注入抽屉内的收件箱查询）。
 */

import { useState } from "react";
import { Badge, Button } from "antd";
import { BellOutlined } from "@ant-design/icons";
import {
  pollIntervalForMode,
  useNotificationStream,
  useUnreadNotificationCount,
} from "../../hooks/useNotifications";
import NotificationDrawer from "./NotificationDrawer";
import styles from "../../styles/notifications.module.css";

/** 界面文案常量（禁止 JSX 内联中文标签字符串）。 */
const NOTIFICATION_BELL_TEXT = {
  ariaLabel: "通知",
} as const;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { mode } = useNotificationStream();
  const refetchInterval = pollIntervalForMode(mode);
  const { data: unreadCount } = useUnreadNotificationCount({ refetchInterval });

  return (
    <>
      <Badge count={unreadCount ?? 0} size="small" offset={[-2, 2]}>
        <Button
          size="middle"
          type="default"
          className={styles.bellButton}
          icon={<BellOutlined />}
          aria-label={NOTIFICATION_BELL_TEXT.ariaLabel}
          onClick={() => setOpen(true)}
          data-testid="notification-bell-button"
        />
      </Badge>
      <NotificationDrawer
        open={open}
        onClose={() => setOpen(false)}
        refetchInterval={refetchInterval}
      />
    </>
  );
}
